import { useCallback, useState } from 'react';
import { Form } from 'react-final-form';
import Modal from 'react-bootstrap/lib/Modal';

import { returnFinalFormError } from 'shared/functions';
import { BaseAttachmentDto, BaseFileTypeDto } from '@eva/types/dto';

import type { UploadComponentBaseProps } from '../types';

// eslint-disable-next-line import/no-cycle
import FormUploadFile, { type FormUploadFileProps, type FormUploadFileValues } from './FormUploadFile';

export type ModalUploadFileOptions<FT> = FormUploadFileProps<FT>;

export type ModalUploadFileProps<FT> = UploadComponentBaseProps<FormUploadFileValues, ModalUploadFileOptions<FT>>;

const ModalUploadFile = <T extends BaseAttachmentDto, FT extends BaseFileTypeDto>(props: ModalUploadFileProps<FT>) => {
  const { show, options, onHide, onSubmit } = props;
  const [isDisabledSubmit, setIsDisabledSubmit] = useState(false);

  const title = options.updateMode ? 'Edit file' : 'Upload file';

  const handleAcceptFiles = (files: []) => {
    typeof files !== 'undefined' && setIsDisabledSubmit(files.length === 0);
  };

  const submit = useCallback(
    (values: FormUploadFileValues) =>
      onSubmit(values)
        .then((fileDto: T) => {
          onHide();
          return Promise.resolve(fileDto);
        })
        .catch(returnFinalFormError(values, { filename: 'files', content: 'files' })),
    [onHide, onSubmit],
  );

  return (
    <Modal show={show} onHide={onHide}>
      <Form
        onSubmit={submit}
        keepDirtyOnReinitialize
        initialValues={options.initialValues}
        render={({ error, dirty, invalid, submitting, submitError, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>{translate(title)}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <FormUploadFile
                fileTypes={options.fileTypes}
                canVerify={options.canVerify}
                updateMode={options.updateMode}
                initialValues={options.initialValues}
                handleAcceptFiles={handleAcceptFiles}
              />
              {error && <div className="text-danger padding">{error}</div>}
              {submitError && <div className="text-danger padding">{submitError}</div>}
            </Modal.Body>

            <Modal.Footer>
              <button
                type="submit"
                disabled={isDisabledSubmit || submitting || (dirty && invalid)}
                className="btn btn-success btn-sm"
              >
                {translate('Save')}
              </button>
              <button type="button" onClick={onHide} className="btn btn-default btn-sm">
                {translate('Cancel')}
              </button>
            </Modal.Footer>
          </form>
        )}
      />
    </Modal>
  );
};

// eslint-disable-next-line import/no-default-export
export default ModalUploadFile;
